import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  DirectorIndentRequestStatus,
  DirectorSpecificIndentList
} from "../../Repository/Api";
import { errorMsg, successMsg } from "../Toastify";

const DirectorIndentspecificsview = () => {
  const { first_name, department, access_token } = useSelector(
    (state) => state?.auth?.auth
  );
  const token = access_token;
  const { id } = useParams();
  const navigate = useNavigate();

  const [specificIndent, setSpecificIndent] = useState({
    product_details: []
  });

  const [tempChanges, setTempChanges] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);

  useEffect(() => {
    getSpecificlist();
  }, []);

  // Convert to number safely
  const toNum = (v) =>
    v === null || v === undefined || v === "" || Number.isNaN(Number(v))
      ? 0
      : Number(v);

  // On-load: default Approve = Applied; Reject = 0 (only for pending rows)
  const getSpecificlist = async () => {
    const response = await DirectorSpecificIndentList(id, token);
    if (response && response.statusCode === 200) {
      const updatedData = {
        ...response.data,
        product_details: (response.data.product_details || []).map((p) => {
          const status = p?.status?.toLowerCase?.() || "pending";
          const isFinal = status === "approved" || status === "rejected";

          const applied = toNum(p.quantity);
          // If row is not finalized, show applied qty in the Approve textbox.
          const approved = isFinal ? toNum(p.approved_quantity) : applied;

          // For pending rows, rejected starts at 0; for finalized, keep server value.
          const rejected = isFinal ? toNum(p.rejected_quantity) : 0;

          return {
            ...p,
            quantity: applied,
            approved_quantity: approved,
            rejected_quantity: rejected
          };
        })
      };

      setSpecificIndent(updatedData);
    } else {
      errorMsg(response.message);
    }
  };

  // When Approve changes, recompute Reject = Applied - Approve (not below 0)
  const handleApproveChange = (newValue, index) => {
    const numericValue = toNum(newValue);
    if (numericValue < 0) {
      setErrorMessages((prev) => {
        const copy = [...prev];
        copy[index] = "Invalid input: Only valid non-negative numbers.";
        return copy;
      });
      return;
    }

    setErrorMessages((prev) => {
      const copy = [...prev];
      copy[index] = null;
      return copy;
    });

    const appliedQty = toNum(specificIndent.product_details[index].quantity);
    const rejectedQty = Math.max(0, appliedQty - numericValue);

    // Update UI state
    setSpecificIndent((prev) => {
      const list = [...prev.product_details];
      list[index] = {
        ...list[index],
        approved_quantity: numericValue,
        rejected_quantity: rejectedQty
      };
      return { ...prev, product_details: list };
    });

    // Track changes for payload
    setTempChanges((prev) => {
      const copy = [...prev];
      copy[index] = {
        ...(copy[index] ?? {}),
        index,
        approved_quantity: numericValue,
        rejected_quantity: rejectedQty
      };
      return copy;
    });
  };

  // Remove keys not needed by API
  const removeIdFromInstances = (data) => {
    const updatedData = {};
    for (const key in data) {
      if (Array.isArray(data[key])) {
        updatedData[key] = data[key].map((item) => {
          const {
            status,
            quantity,
            hold_quantity,
            issued_quantity,
            already_acknowledged_quantity,
            issued_quantity_status,
            product_issued_log,
            product_name,
            remaining_quantity_in_store,
            ...rest
          } = item;
          return rest;
        });
      } else {
        updatedData[key] = data[key];
      }
    }
    return updatedData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const mainKey = "product_details";

    const Payload = { ...specificIndent };
    const clonedSpecificIndent = { ...specificIndent };

    // Strip top-level fields the API doesn't want
    delete Payload.created_at;
    delete Payload.created_by;
    delete Payload.department;
    delete Payload.department_id;
    delete Payload.director_comment;
    delete Payload.director_status;
    delete Payload.employee_name;
    delete Payload.hod_comment;
    delete Payload.hod_status;
    delete Payload.indent_id;
    delete Payload.indent_number;
    delete Payload.is_deleted;
    delete Payload.requested_date;
    delete Payload.status;
    delete Payload.updated_at;
    delete Payload.updated_by;

    const modifiedData = tempChanges
      .filter((change) => change && change.index !== undefined)
      .map((change) => {
        const { index } = change;
        if (
          clonedSpecificIndent[mainKey] &&
          clonedSpecificIndent[mainKey][index] &&
          Object.keys(clonedSpecificIndent[mainKey][index]).some(
            (v) => clonedSpecificIndent[mainKey][index][v] !== change[v]
          )
        ) {
          const { index: _drop, ...rest } = change;
          return {
            product_id: clonedSpecificIndent[mainKey][index].product_id,
            ...rest
          };
        }
        return null;
      })
      .filter(Boolean);

    const pay = { id, [mainKey]: modifiedData };
    const modifiedPayload = removeIdFromInstances(pay);

    const response = await DirectorIndentRequestStatus(modifiedPayload, token);
    if (response && response.statusCode === 200) {
      successMsg(response.message);
      getSpecificlist();
    } else {
      errorMsg(response.message);
    }
  };

  return (
    <>
      <div className="">
        <section className="top-content-container">
          <div className="my-4">
            <div className="row">
              <div className="col-lg-6">
                <h6 className="headertext">{first_name}</h6>
                <h6 className="subheadertext">{department}</h6>
              </div>
            </div>
          </div>
        </section>

        <section>
          <div className="row">
            <div className="">
              <div className="graphbox">
                <h6 className="labeltitle">
                  <span className="arrowicon" onClick={() => navigate(-1)}>
                    ← &nbsp;
                  </span>
                  Back To Indent List
                </h6>

                <div className="mt-5">
                  <h5 className="labeltitle centertext">
                    Indent Form Specific View
                  </h5>

                  <div className="row mt-5">
                    <div className="col-lg-4 col-md-5">
                      <h6>1. Indentor Name</h6>
                    </div>
                    <div className="col-lg-1 p-0">:</div>
                    <div className="col-lg-5">
                      <h6>{specificIndent?.employee_name}</h6>
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div className="col-lg-4 col-md-5">
                      <h6>2. Department</h6>
                    </div>
                    <div className="col-lg-1 p-0">:</div>
                    <div className="col-lg-5">
                      <h6>{specificIndent?.department}</h6>
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div className="col-lg-4 col-md-5">
                      <h6>3. Indent Number</h6>
                    </div>
                    <div className="col-lg-1 p-0">:</div>
                    <div className="col-lg-5">
                      <h6>
                        {specificIndent?.indent_number
                          ? specificIndent?.indent_number
                          : "NA"}
                      </h6>
                    </div>
                  </div>

                  <div className="row mt-5">
                    <div className="col-lg-4 col-md-5">
                      <h6>4. Indent Raised Date</h6>
                    </div>
                    <div className="col-lg-1 p-0">:</div>
                    <div className="col-lg-5">
                      <h6>
                        {moment(specificIndent?.requested_date).format(
                          "DD-MM-yyyy"
                        )}
                      </h6>
                    </div>
                  </div>

                  {specificIndent?.hod_comment ? (
                    <div className="row mt-5">
                      <div className="col-lg-4 col-md-5">
                        <h6>5. HOD Comment</h6>
                      </div>
                      <div className="col-lg-1 p-0">:</div>
                      <div className="col-lg-5">
                        <h6>{specificIndent?.hod_comment || "NA"}</h6>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}

                  {specificIndent?.director_comment ? (
                    <div className="row mt-5">
                      <div className="col-lg-4 col-md-5">
                        <h6>6. Director Comment</h6>
                      </div>
                      <div className="col-lg-1 p-0">:</div>
                      <div className="col-lg-5">
                        <h6>{specificIndent?.director_comment || "NA"}</h6>
                      </div>
                    </div>
                  ) : (
                    ""
                  )}
                </div>

                <div className="mt-5">
                  <h5 className="my-5">Product List</h5>
                </div>

                <div>
                  <form>
                    <div className="scroll-table">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th scope="col">S.No</th>
                            <th scope="col">Product Name</th>
                            <th scope="col">Applied Quantity</th>
                            <th scope="col">Approved Quantity</th>
                            <th scope="col">Rejected Quantity</th>
                            <th scope="col">Hold Quantity</th>
                            <th scope="col">Avilable Quantity in Store</th>
                            <th scope="col">Approve</th>
                            <th scope="col">Reject</th>
                          </tr>
                        </thead>
                        <tbody>
                          {specificIndent?.product_details?.map(
                            (product, index) => (
                              <tr key={product.id || index}>
                                <td>{index + 1}</td>
                                <td className="textformat">
                                  {product?.product_name}
                                </td>
                                <td>{product?.quantity ?? "-"}</td>
                                <td>{product?.approved_quantity ?? "-"}</td>
                                <td>{product?.rejected_quantity ?? "-"}</td>
                                <td>{product?.hold_quantity ?? "-"}</td>
                                <td>
                                  {product?.remaining_quantity_in_store ?? 0}
                                </td>

                                {/* Approve input shows Applied on load; editable */}
                                <td>
                                  {product?.status === "approved" ||
                                  product?.status === "rejected" ? (
                                    "NA"
                                  ) : (
                                    <>
                                      <input
                                        type="number"
                                        className="fieldinputreport"
                                        value={product.approved_quantity}
                                        onChange={(e) =>
                                          handleApproveChange(
                                            e.target.value,
                                            index
                                          )
                                        }
                                      />
                                      {errorMessages[index] && (
                                        <small style={{ color: "red" }}>
                                          {errorMessages[index]}
                                        </small>
                                      )}
                                    </>
                                  )}
                                </td>

                                {/* Reject shows auto-calculated value */}
                                <td>
                                  {product?.status === "approved" ||
                                  product?.status === "rejected" ? (
                                    "NA"
                                  ) : (
                                    <input
                                      type="number"
                                      className="fieldinputreport"
                                      value={product.rejected_quantity ?? 0}
                                      readOnly
                                    />
                                  )}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>

                    {specificIndent?.director_status === "approved" ? (
                      ""
                    ) : (
                      <div className="row my-5">
                        <div className="col-lg-5 mx-auto my-auto centertext">
                          <button
                            type="submit"
                            className="approvebutton"
                            onClick={(e) => handleSubmit(e)}
                          >
                            Update
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DirectorIndentspecificsview;
